/*  CourseReviewDetail — Admin course preview + approve/reject decision.
    ────────────────────────────────────────────────────────────────────────
    Route: /admin/course-reviews/:courseId
    Access: admin + super_admin.

    Layout:
      • Course header (title, author, track, phase, duration)
      • Left panel: expandable module → lesson tree (read-only view)
      • Right rail: decision panel (approve / reject with feedback) + review
                    history (past submissions and decisions)
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, CheckCircle, XCircle, VideoCamera, Article, FileArrowUp,
    Question, User as UserIcon, Clock, ClockCounterClockwise, CaretDown, CaretRight,
    ChatText, ListChecks, ToggleLeft, Sparkle, WarningOctagon,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { confirmDialog, promptDialog } from "@/lib/confirm";
import CourseActivityFeed from "@/components/creator/CourseActivityFeed";
import RichTextView, { expandEmbeds } from "@/components/creator/RichTextView";


const LESSON_TYPE_META = {
    video: { label: "Video",  Icon: VideoCamera, tone: "bg-sky-50 text-sky-700 border-sky-200" },
    text:  { label: "Text",   Icon: Article,     tone: "bg-violet-50 text-violet-700 border-violet-200" },
    file:  { label: "File",   Icon: FileArrowUp, tone: "bg-amber-50 text-amber-700 border-amber-200" },
    quiz:  { label: "Quiz",   Icon: Question,    tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const Q_TYPE_META = {
    mcq:        { label: "MCQ",         Icon: ListChecks },
    true_false: { label: "True/False",  Icon: ToggleLeft },
    scenario:   { label: "Scenario",    Icon: Sparkle },
};


export default function CourseReviewDetail() {
    const { courseId } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [decision, setDecision] = useState(null); // "approve" | "reject"
    const [feedback, setFeedback] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/course-review/courses/${courseId}`);
            setCourse(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load course.");
            navigate("/admin/course-reviews");
        } finally {
            setLoading(false);
        }
    }, [courseId, navigate]);

    useEffect(() => { load(); }, [load]);

    const stats = useMemo(() => {
        if (!course) return { modules: 0, lessons: 0, quizzes: 0 };
        const modules = course.modules?.length || 0;
        let lessons = 0, quizzes = 0;
        (course.modules || []).forEach((m) => {
            (m.lessons || []).forEach((l) => {
                lessons += 1;
                if (l.type === "quiz") quizzes += 1;
            });
        });
        return { modules, lessons, quizzes };
    }, [course]);

    const onSubmit = async () => {
        if (!decision) return;
        if (decision === "reject" && !feedback.trim()) {
            toast.error("Please write feedback for the author before rejecting.");
            return;
        }
        const ok = await confirmDialog({
            title: decision === "approve" ? "Approve & publish?" : "Reject with feedback?",
            description: decision === "approve"
                ? "The course will go live on the doctor's Learning Academy immediately. You can pull it back with 'Unpublish' if needed."
                : "The course will move back to the author's Drafts with your feedback attached. They can revise and resubmit.",
            confirmLabel: decision === "approve" ? "Approve & publish" : "Send back with feedback",
            variant: decision === "reject" ? "destructive" : undefined,
        });
        if (!ok) return;

        try {
            setSubmitting(true);
            await api.post(`/admin/course-review/courses/${courseId}/decision`, {
                decision,
                feedback: feedback.trim(),
            });
            toast.success(decision === "approve" ? "Approved & published" : "Sent back to author");
            navigate("/admin/course-reviews");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't submit decision.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || !course) {
        return (
            <div className="p-8 space-y-4">
                <div className="h-8 w-40 bg-secondary rounded animate-pulse" />
                <div className="h-96 bg-secondary rounded animate-pulse" />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-8" data-testid="course-review-detail">
            {/* Breadcrumb */}
            <Link
                to="/admin/course-reviews"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors no-underline"
                data-testid="review-back-link"
            >
                <ArrowLeft size={11} weight="bold" /> Course Reviews
            </Link>

            {/* Header */}
            <div className="mt-3 bg-card border border-border rounded-2xl p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Course</div>
                        <h1 className="font-display text-3xl font-bold tracking-tight mt-0.5" data-testid="review-course-title">
                            {course.title}
                        </h1>
                        {course.description && (
                            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{course.description}</p>
                        )}
                        <div className="mt-3 flex items-center gap-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider flex-wrap">
                            <span className="inline-flex items-center gap-1"><UserIcon size={11} weight="duotone" /> {course.author_name}</span>
                            {course.track && <span>Track: <span className="text-foreground">{course.track}</span></span>}
                            {course.phase && <span>Phase: <span className="text-foreground">{course.phase}</span></span>}
                            {course.duration_minutes ? <span>{course.duration_minutes} min</span> : null}
                            <span>{stats.modules}m · {stats.lessons}l · {stats.quizzes} quiz</span>
                        </div>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-bold uppercase tracking-wider ${
                        course.status === "under_review" ? "bg-amber-50 text-amber-800 border-amber-200" :
                        course.status === "published"     ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                        course.status === "rejected"      ? "bg-rose-50 text-rose-800 border-rose-200" :
                                                            "bg-slate-100 text-slate-700 border-slate-200"
                    }`} data-testid="review-course-status">
                        <ClockCounterClockwise size={10} weight="fill" /> {course.status.replace("_", " ")}
                    </span>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
                {/* Course tree preview */}
                <div className="bg-card border border-border rounded-2xl p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">
                        Content preview
                    </div>
                    {(course.modules || []).length === 0 ? (
                        <div className="py-8 text-center text-sm text-muted-foreground">This course has no modules.</div>
                    ) : (
                        <div className="space-y-2">
                            {course.modules.map((m, mIdx) => <ModuleBlock key={m.id} module={m} index={mIdx} />)}
                        </div>
                    )}
                </div>

                {/* Decision + history rail */}
                <aside className="space-y-4">
                    {course.status === "under_review" && (
                        <DecisionPanel
                            decision={decision}
                            setDecision={setDecision}
                            feedback={feedback}
                            setFeedback={setFeedback}
                            onSubmit={onSubmit}
                            submitting={submitting}
                        />
                    )}
                    {course.status === "published" && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm text-emerald-900 space-y-2" data-testid="review-published-panel">
                            <div className="font-bold flex items-center gap-1.5"><CheckCircle size={13} weight="fill" /> Course is live</div>
                            <p className="text-xs">Doctors can enroll now. Use &quot;Unpublish&quot; if a critical revision is required.</p>
                            <Button variant="outline" size="sm" onClick={async () => {
                                const reason = await promptDialog({
                                    title: "Unpublish this course?",
                                    message: "The course will be removed from the doctor's catalogue and reset to Draft. Please tell the author why so they can revise.",
                                    placeholder: "e.g. Module 2 lesson 3 diagram is out of date · pricing needs review…",
                                    confirmText: "Unpublish",
                                    cancelText: "Keep live",
                                    tone: "warn",
                                    multiline: true,
                                    minLength: 10,
                                    maxLength: 500,
                                    required: true,
                                    testid: "review-unpublish-prompt",
                                });
                                if (reason == null) return;
                                try {
                                    await api.post(`/admin/course-review/courses/${courseId}/unpublish`, { reason: reason.trim() });
                                    toast.success("Course unpublished");
                                    navigate("/admin/course-reviews");
                                } catch (e) {
                                    toast.error(e?.response?.data?.detail || "Couldn't unpublish.");
                                }
                            }} data-testid="review-unpublish-btn">
                                Unpublish course
                            </Button>
                        </div>
                    )}
                    <ReviewHistory reviews={course.reviews || []} />
                    <CourseActivityFeed
                        courseId={courseId}
                        endpoint={`/admin/course-review/courses/${courseId}/activity`}
                        title="Activity monitor"
                        testid="review-activity-feed"
                    />
                </aside>
            </div>
        </div>
    );
}


function DecisionPanel({ decision, setDecision, feedback, setFeedback, onSubmit, submitting }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-4 space-y-3 sticky top-4" data-testid="review-decision-panel">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Your decision
            </div>
            <div className="grid grid-cols-2 gap-2">
                <button
                    type="button"
                    onClick={() => setDecision("approve")}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                        decision === "approve"
                            ? "bg-emerald-50 border-emerald-500 text-emerald-900 shadow-sm"
                            : "bg-white border-border hover:border-emerald-300 text-foreground"
                    }`}
                    data-testid="review-decision-approve"
                >
                    <CheckCircle size={16} weight="fill" className={decision === "approve" ? "text-emerald-600" : "text-muted-foreground"} />
                    <div className="mt-1 text-sm font-bold">Approve</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">Publish to doctors</div>
                </button>
                <button
                    type="button"
                    onClick={() => setDecision("reject")}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                        decision === "reject"
                            ? "bg-rose-50 border-rose-500 text-rose-900 shadow-sm"
                            : "bg-white border-border hover:border-rose-300 text-foreground"
                    }`}
                    data-testid="review-decision-reject"
                >
                    <XCircle size={16} weight="fill" className={decision === "reject" ? "text-rose-600" : "text-muted-foreground"} />
                    <div className="mt-1 text-sm font-bold">Send back</div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">Request revisions</div>
                </button>
            </div>

            {decision && (
                <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Feedback {decision === "reject" && <span className="text-rose-600">*</span>}
                        <span className="ml-1 normal-case tracking-normal text-muted-foreground font-medium">
                            — {decision === "approve" ? "(optional)" : "(required)"}
                        </span>
                    </label>
                    <Textarea
                        rows={5}
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        placeholder={
                            decision === "approve"
                                ? "Nice work! Any notes for the author (optional)…"
                                : "Explain what needs to change before this can be published."
                        }
                        className="mt-1"
                        maxLength={2000}
                        data-testid="review-feedback-input"
                    />
                </div>
            )}

            <Button
                onClick={onSubmit}
                disabled={!decision || submitting || (decision === "reject" && !feedback.trim())}
                className="w-full"
                data-testid="review-submit-btn"
            >
                {submitting
                    ? "Submitting…"
                    : decision === "approve"
                    ? "Approve & publish"
                    : decision === "reject"
                    ? "Send back with feedback"
                    : "Pick a decision"}
            </Button>
        </div>
    );
}


function ModuleBlock({ module, index }) {
    const [open, setOpen] = useState(index === 0);
    const lessonCount = (module.lessons || []).length;
    return (
        <div className="border border-border rounded-xl overflow-hidden" data-testid={`review-module-${module.id}`}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-secondary/40 transition-colors"
                data-testid={`review-module-toggle-${module.id}`}
            >
                <div className="w-7 h-7 shrink-0 rounded-md bg-primary/10 text-primary text-[11px] font-bold tabular-nums grid place-items-center">
                    {index + 1}
                </div>
                <div className="flex-1 text-left min-w-0">
                    <div className="text-sm font-bold truncate">{module.title}</div>
                    <div className="text-[11px] text-muted-foreground">
                        {lessonCount} lesson{lessonCount === 1 ? "" : "s"}
                        {module.is_locked ? " · locked" : ""}
                    </div>
                </div>
                {open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
            </button>
            {open && (
                <div className="border-t border-border bg-secondary/20 p-3 space-y-2">
                    {lessonCount === 0 ? (
                        <div className="text-xs text-muted-foreground italic px-2 py-3">This module has no lessons.</div>
                    ) : (
                        module.lessons.map((l, lIdx) => <LessonBlock key={l.id} lesson={l} index={lIdx} />)
                    )}
                </div>
            )}
        </div>
    );
}


function LessonBlock({ lesson, index }) {
    const meta = LESSON_TYPE_META[lesson.type] || LESSON_TYPE_META.text;
    const [open, setOpen] = useState(false);
    return (
        <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid={`review-lesson-${lesson.id}`}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-secondary/40 transition-colors"
                data-testid={`review-lesson-toggle-${lesson.id}`}
            >
                <span className={`w-6 h-6 shrink-0 rounded-md grid place-items-center text-[10px] font-bold ${meta.tone}`}>
                    <meta.Icon size={11} weight="fill" />
                </span>
                <div className="flex-1 text-left min-w-0">
                    <div className="text-xs font-semibold truncate">{index + 1}. {lesson.title}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{meta.label}</div>
                </div>
                {open ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
            </button>
            {open && <LessonBody lesson={lesson} />}
        </div>
    );
}


function LessonBody({ lesson }) {
    if (lesson.type === "video") {
        return (
            <div className="border-t border-border p-3 text-xs space-y-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Video URL</div>
                {lesson.video_url ? (
                    <a href={lesson.video_url} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">
                        {lesson.video_url}
                    </a>
                ) : (
                    <div className="text-rose-600 italic">Missing — author has not added a URL.</div>
                )}
            </div>
        );
    }
    if (lesson.type === "text") {
        return (
            <div className="border-t border-border p-3 text-xs">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Content</div>
                {lesson.text_body ? (
                    <div className="prose prose-sm max-w-none text-foreground [&_*]:!my-1" dangerouslySetInnerHTML={{ __html: expandEmbeds(lesson.text_body) }} />
                ) : (
                    <div className="text-rose-600 italic">Empty text body.</div>
                )}
            </div>
        );
    }
    if (lesson.type === "file") {
        return (
            <div className="border-t border-border p-3 text-xs space-y-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Attached file</div>
                {lesson.file_media_id ? (
                    <div className="flex items-center gap-2">
                        <FileArrowUp size={14} weight="duotone" className="text-primary" />
                        <span className="font-mono text-[10px]">Media ID: {lesson.file_media_id.slice(0, 12)}…</span>
                    </div>
                ) : (
                    <div className="text-rose-600 italic">No file attached.</div>
                )}
            </div>
        );
    }
    if (lesson.type === "quiz") {
        const quiz = lesson.quiz;
        if (!quiz || !quiz.questions?.length) {
            return (
                <div className="border-t border-border p-3 text-xs text-rose-600 italic">
                    No questions added to this quiz.
                </div>
            );
        }
        return (
            <div className="border-t border-border p-3 text-xs space-y-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {quiz.questions.length} question{quiz.questions.length === 1 ? "" : "s"} · pass {Math.round((quiz.pass_score || 0) * 100)}% · {quiz.max_attempts} attempts
                </div>
                {quiz.questions.map((q, i) => {
                    const qMeta = Q_TYPE_META[q.type] || Q_TYPE_META.mcq;
                    return (
                        <div key={i} className="p-2 bg-secondary/40 rounded border border-border" data-testid={`review-quiz-q-${i}`}>
                            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                                <qMeta.Icon size={10} weight="fill" /> {qMeta.label} · Q{i + 1}
                            </div>
                            <RichTextView html={q.prompt} className="text-xs font-semibold mb-1.5" />
                            <div className="space-y-0.5">
                                {(q.options || []).map((opt, oIdx) => {
                                    const isCorrect = q.answer_index === oIdx;
                                    return (
                                        <div key={oIdx} className={`text-[11px] flex items-center gap-1.5 pl-1 ${isCorrect ? "text-emerald-700 font-semibold" : "text-foreground/80"}`}>
                                            {isCorrect
                                                ? <CheckCircle size={10} weight="fill" className="text-emerald-500 shrink-0" />
                                                : <span className="w-2.5 h-2.5 rounded-full border border-slate-300 inline-block shrink-0" />}
                                            <span>{opt}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            {q.rationale && (
                                <div className="mt-1.5 pt-1.5 border-t border-border text-[10px] text-muted-foreground italic">
                                    <span className="font-semibold not-italic">Rationale:</span>
                                    <RichTextView html={q.rationale} className="not-italic" />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }
    return null;
}


function ReviewHistory({ reviews }) {
    if (!reviews.length) return null;
    return (
        <div className="bg-card border border-border rounded-2xl p-4" data-testid="review-history">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">
                Review history
            </div>
            <div className="space-y-3">
                {reviews.map((r, i) => (
                    <div key={r.id || i} className="text-xs" data-testid={`review-history-item-${i}`}>
                        <div className="flex items-center gap-2 font-semibold">
                            {r.decision === "approve" && <CheckCircle size={12} weight="fill" className="text-emerald-500" />}
                            {r.decision === "reject" && <WarningOctagon size={12} weight="fill" className="text-rose-500" />}
                            {!r.decision && <Clock size={12} weight="fill" className="text-amber-500" />}
                            <span>{r.decision ? r.decision : "Pending"}</span>
                            {r.decider_name && <span className="text-muted-foreground font-normal">by {r.decider_name}</span>}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                            Submitted {new Date(r.submitted_at).toLocaleString()}
                            {r.decided_at ? ` · Decided ${new Date(r.decided_at).toLocaleString()}` : ""}
                        </div>
                        {r.submitter_note && (
                            <div className="mt-1 p-2 bg-secondary/40 rounded text-[11px] italic">
                                <ChatText size={9} weight="duotone" className="inline mr-1" />
                                Author note: "{r.submitter_note}"
                            </div>
                        )}
                        {r.feedback && (
                            <div className="mt-1 p-2 bg-secondary/40 rounded text-[11px]">
                                Admin feedback: {r.feedback}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
